import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import { VehicleBondInformationDto, vehicleBondInformationValidationSchema, VehicleDto } from 'dtos'
import { Formik } from 'formik'
import { formatCurrency } from 'utils'

interface BondInformationAddEditDialogProps {
  disabled?: boolean
  initialValues: VehicleBondInformationDto
  open: boolean
  onClose: () => void
  onSave: (values: any) => void
  isLoading: boolean
}

export default function BondInformationAddEdit({
  disabled,
  initialValues,
  isLoading,
  onClose,
  onSave,
  open
}: BondInformationAddEditDialogProps) {
  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur={true}
        validateOnChange={true}
        validationSchema={vehicleBondInformationValidationSchema}
      >
        {({ errors, handleBlur, handleChange, isSubmitting, handleSubmit, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Add Special Bond #</DialogTitle>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading || (initialValues.amountUSD ?? 0) < (initialValues.bondThreshold ?? 0) || disabled}
                      error={Boolean(touched.specialBondNumber && errors.specialBondNumber)}
                      fullWidth
                      helperText={touched.specialBondNumber && errors.specialBondNumber}
                      label='Special Bond #'
                      name='specialBondNumber'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.specialBondNumber}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField
                      label='Amount (USD)'
                      value={initialValues.amountUSD ? formatCurrency(initialValues.amountUSD) : ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField
                      label='Bond Threshold'
                      value={initialValues.bondThreshold ? formatCurrency(initialValues.bondThreshold) : ''}
                    />
                  </Grid>

                  {(initialValues.amountUSD ?? 0) >= (initialValues.bondThreshold ?? 0) ? (
                    <Grid item xs={12}>
                      <Typography variant='body2' color='error'>
                        Vehicle Amount exceeds Bond Threshold.
                      </Typography>

                      <Typography variant='body2' color='error'>
                        Special Bond # is required.
                      </Typography>
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant='body2'>Vehicle Amount is less than Bond Threshold.</Typography>

                      <Typography variant='body2'>
                        The next available Bond Number from the current Bond Block will be assigned when the vehicle is locked.
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <ExplanationAccordion variant='outlined'>
                    Special Bond # is an input field. || The Amount (USD) label shows the Amount (USD) value from the Add/Edit
                    Vehicle Dialog. || The Bond Threshold label shows the Bond Threshold value from the Bond Policy card under
                    Settings. || If the vehicle amount is greater than or equal to the Bond Threshold, there will be text
                    saying, “Vehicle Amount exceeds Bond Threshold. Special Bond # is required”. || If the vehicle amount is
                    less than the Bond Threshold, there will be black text saying, “Vehicle Amount is less than Bond Threshold.
                    The next available Bond Number from the current Bond Block will be assigned when the vehicle is locked.” ||
                    If the vehicle Amount (USD) is less than the Bond Threshold set under Settings, the Add Special Bond #
                    dialog's SAVE button cannot be pressed.
                  </ExplanationAccordion>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton
                  disabled={(initialValues.amountUSD ?? 0) < (initialValues.bondThreshold ?? 0) || disabled}
                  // !(vehicle?.isLocked === false) ||

                  // If the vehicle's Amount (USD) is less than the Bond Threshold set under Setings,
                  // the SAVE button cannot be pressed.
                  // Maxwell Sommer 09/10/2024
                  loading={isLoading || isSubmitting}
                  type='submit'
                  variant='contained'
                >
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
